// src/pages/TradeOffers.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { collection, getDocs, onSnapshot, query, where, doc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { UserContext } from '../contexts/UserContext';
import { hydrateCard, hydrateInstances } from '../utilities';
import { PlayingCard } from '../components/PlayingCard';
import CardInfoDialog from '../components/CardInfoDialog';

// ---------------- Theme Tokens (match Beasts of Legend) ----------------
const COL = {
    bgStart: '#000000',
    bgEnd: '#0a0a0a',
    gold: '#d4af37',
    goldSoft: 'rgba(212,175,55,0.75)',
    goldDim: 'rgba(212,175,55,0.6)',
    text: '#f9f6e3',
    panel: 'rgba(0,0,0,0.70)',
};
const fonts = { title: "'Cinzel', serif" };
const glow = (s = 0.3) => `0 0 24px rgba(212,175,55,${s}), 0 0 4px rgba(212,175,55,${s})`;

const Section = styled(Box)(({ theme }) => ({
    background: COL.panel,
    border: `1px solid ${COL.goldDim}`,
    borderRadius: 12,
    boxShadow: `0 8px 24px rgba(212,175,55,0.15)`,
    padding: theme.spacing(2.5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontFamily: fonts.title,
    letterSpacing: '0.8px',
    color: COL.text,
    textShadow: glow(0.25),
}));

const GoldDivider = styled(Divider)({
    borderColor: COL.goldDim,
    opacity: 0.6,
});

const GoldButton = styled(Button)(({ theme }) => ({
    fontFamily: fonts.title,
    letterSpacing: '0.6px',
    fontWeight: 600,
    color: COL.bgEnd,
    background: `linear-gradient(180deg, ${COL.goldSoft}, ${COL.gold})`,
    border: `1px solid ${COL.gold}`,
    boxShadow: glow(0.25),
    textTransform: 'none',
    '&:hover': {
        boxShadow: glow(0.4),
        filter: 'brightness(1.05)',
        background: `linear-gradient(180deg, ${COL.gold}, ${COL.gold})`,
    },
    '&:disabled': {
        opacity: 0.5,
        color: '#3b3b3b',
        background: 'linear-gradient(180deg, #5f5630, #4b421d)',
    },
}));

const GhostButton = styled(Button)(({ theme }) => ({
    fontFamily: fonts.title,
    color: COL.text,
    border: `1px solid ${COL.goldDim}`,
    textTransform: 'none',
    background: 'transparent',
    '&:hover': {
        background: 'rgba(212,175,55,0.08)',
        boxShadow: glow(0.25),
    },
}));

const GoldTextField = styled(TextField)({
    '& label': { color: COL.goldDim, fontFamily: fonts.title },
    '& label.Mui-focused': { color: COL.gold },
    '& .MuiInputBase-input': { color: COL.text },
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: COL.goldDim },
        '&:hover fieldset': { borderColor: COL.gold },
        '&.Mui-focused fieldset': { borderColor: COL.gold, boxShadow: glow(0.3) },
    },
});

// ---------------- API helpers ----------------
const api = {
    async call(name, body) {
        const idToken = await getAuth().currentUser?.getIdToken();
        const url = `https://us-central1-beasts-of-legend-bc9bd.cloudfunctions.net/${name}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify(body || {}),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.error) throw new Error(json?.error || 'error');
        return json;
    },
    propose(payload) { return api.call('proposeTrade', payload); },
    accept(tradeId) { return api.call('acceptTrade', { tradeId }); },
    decline(tradeId) { return api.call('declineTrade', { tradeId }); },
    cancel(tradeId) { return api.call('cancelTrade', { tradeId }); },

    // returns { soulDust, cards: [{instanceId, cardId, level, evolutionStage}, ...] }
    listTradables(userId) { return api.call('listTradables', { userId }); },
};

export default function TradeOffers() {
    const { userData, globalCards } = useContext(UserContext);
    const uid = userData?.uid || '';
    const myDust = Number(userData?.consumables?.soulDust || 0);

    // 🔒 Browse gate: disable ALL interactions unless true (live from Firestore)
    const [allowBrowse, setAllowBrowse] = useState(userData?.privacy?.allowTradeBrowse ?? true);
    useEffect(() => {
        if (!uid) { setAllowBrowse(true); return; }
        const userRef = doc(db, 'users', uid);
        const unsub = onSnapshot(
            userRef,
            (snap) => {
                const val = snap.data()?.privacy?.allowTradeBrowse;
                setAllowBrowse(val === undefined ? true : !!val);
            },
            () => setAllowBrowse(true)
        );
        return () => unsub();
    }, [uid]);

    // Recipient search
    const [userQuery, setUserQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [recipientId, setRecipientId] = useState('');
    const [recipientName, setRecipientName] = useState('');

    // Offer builder state
    const [offerCreatorCards, setOfferCreatorCards] = useState([]);
    const [offerRecipientCards, setOfferRecipientCards] = useState([]);
    const [offerCreatorDust, setOfferCreatorDust] = useState(0);
    const [offerRecipientDust, setOfferRecipientDust] = useState(0);
    const [message, setMessage] = useState('');

    // Snackbar
    const [snack, setSnack] = useState({ open: false, msg: '' });
    const showSnack = (msg) => setSnack({ open: true, msg });

    // Owned/hydrated
    const ownedInstances = userData?.ownedCards || [];
    const ownedHydrated = useMemo(
        () => hydrateInstances(ownedInstances, globalCards),
        [ownedInstances, globalCards]
    );

    const hydrateList = (arr) => (arr || []).map(c => hydrateCard(c, globalCards) || c);

    // Card inspect dialog (trade-only CTA)
    const [inspectOpen, setInspectOpen] = useState(false);
    const [inspectCard, setInspectCard] = useState(null);
    const [inspectInstance, setInspectInstance] = useState(null);
    const [inspectSide, setInspectSide] = useState('creator'); // 'creator' | 'recipient'

    // ⭐ NEW: read-only viewer for active/open trades (NO buttons)
    const [tradeViewOpen, setTradeViewOpen] = useState(false);
    const [tradeViewCard, setTradeViewCard] = useState(null);
    const openTradeView = (hydratedCard) => {
        if (!hydratedCard) return;
        setTradeViewCard(hydratedCard);
        setTradeViewOpen(true);
    };
    const closeTradeView = () => {
        setTradeViewOpen(false);
        setTradeViewCard(null);
    };

    const openInspect = (hydratedCard, instance, side) => {
        if (!allowBrowse) return;
        if (!hydratedCard || !instance) return;
        setInspectCard(hydratedCard);
        setInspectInstance(instance);
        setInspectSide(side);
        setInspectOpen(true);
    };

    const closeInspect = () => {
        setInspectOpen(false);
        setInspectCard(null);
        setInspectInstance(null);
        setInspectSide('creator');
    };

    const addOfferCard = (instance, side) => {
        if (!instance || !allowBrowse) return;

        if (side === 'creator') {
            if (offerCreatorCards.find(c => c.instanceId === instance.instanceId)) return;
            setOfferCreatorCards([...offerCreatorCards, instance]);
        } else {
            if (offerRecipientCards.find(c => c.instanceId === instance.instanceId)) return;
            setOfferRecipientCards([...offerRecipientCards, instance]);
        }
    };

    const removeOfferCard = (instanceId, side) => {
        if (side === 'creator') setOfferCreatorCards(offerCreatorCards.filter(c => c.instanceId !== instanceId));
        else setOfferRecipientCards(offerRecipientCards.filter(c => c.instanceId !== instanceId));
    };

    // 🔎 Search public users by username (client-side filter over mirror)
    useEffect(() => {
        let active = true;
        (async () => {
            if (!userQuery.trim()) { setSearchResults([]); return; }
            const snap = await getDocs(query(collection(db, 'publicUsers')));
            const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const filtered = all
                .filter(u => (u.username || '').toLowerCase().includes(userQuery.toLowerCase()))
                .slice(0, 8);
            if (active) setSearchResults(filtered);
        })();
        return () => { active = false; };
    }, [userQuery]);

    // 🔁 Subscribe to open trades (where I am creator OR recipient)
    const [trades, setTrades] = useState([]);
    useEffect(() => {
        if (!uid) return;

        const creatorQuery = query(collection(db, 'tradeOffers'), where('creatorId', '==', uid));
        const recipientQuery = query(collection(db, 'tradeOffers'), where('recipientId', '==', uid));

        const unsubCreator = onSnapshot(creatorQuery, snapshot => {
            const openTrades = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(t => t.status === 'open');

            setTrades(prev => {
                const others = prev.filter(t => t.creatorId !== uid);
                return [...openTrades, ...others];
            });
        });

        const unsubRecipient = onSnapshot(recipientQuery, snapshot => {
            const openTrades = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(t => t.status === 'open');

            setTrades(prev => {
                const others = prev.filter(t => t.recipientId !== uid);
                return [...others, ...openTrades];
            });
        });

        return () => { unsubCreator(); unsubRecipient(); };
    }, [uid]);

    // ─────────────────────────────────────────
    // Recipient tradables (safe server view)
    // ─────────────────────────────────────────
    const [recipientTradables, setRecipientTradables] = useState([]);
    const [recipientDust, setRecipientDust] = useState(0);
    const recipientHydrated = useMemo(
        () => hydrateInstances(recipientTradables, globalCards),
        [recipientTradables, globalCards]
    );

    useEffect(() => {
        let alive = true;
        (async () => {
            setRecipientTradables([]);
            setRecipientDust(0);
            if (!recipientId) return;

            try {
                const { soulDust, cards } = await api.listTradables(recipientId);
                if (!alive) return;
                setRecipientTradables(Array.isArray(cards) ? cards : []);
                setRecipientDust(Number(soulDust || 0));
            } catch (e) {
                showSnack(
                    e?.message === 'browse-not-allowed'
                        ? 'That player has disabled trading browse.'
                        : (e?.message || 'Could not load recipient inventory')
                );
            }
        })();
        return () => { alive = false; };
    }, [recipientId]);

    const submitTrade = async () => {
        try {
            if (!allowBrowse) { showSnack('Trading is disabled. Turn it on in your Profile.'); return; }
            if (!recipientId) { showSnack('Pick a recipient'); return; }
            if (offerCreatorDust < 0 || offerRecipientDust < 0) { showSnack('Dust must be ≥ 0'); return; }
            if (offerCreatorDust > myDust) { showSnack('Not enough Soul Dust'); return; }

            await api.propose({
                recipientId,
                creatorUsername: userData?.username || '',
                recipientUsername: recipientName || '',
                message,
                offerFromCreator: { cards: offerCreatorCards, soulDust: Number(offerCreatorDust || 0) },
                offerFromRecipient: { cards: offerRecipientCards, soulDust: Number(offerRecipientDust || 0) },
            });

            setOfferCreatorCards([]);
            setOfferRecipientCards([]);
            setOfferCreatorDust(0);
            setOfferRecipientDust(0);
            setMessage('');
            showSnack('Trade sent!');
        } catch (e) {
            showSnack(e?.message || 'Failed to send trade');
        }
    };

    const handleInspectAdd = () => {
        if (!inspectInstance) return;
        addOfferCard(inspectInstance, inspectSide);
        closeInspect();
    };

    const addCtaLabel = inspectSide === 'creator' ? 'Add to Trade (Your Offer)' : 'Add to Trade (Their Offer)';

    return (
        <Section sx={{ mt: 4 }}>
            <SectionTitle variant="h5" gutterBottom>Trade Offers</SectionTitle>
            <GoldDivider sx={{ mb: 2 }} />

            {/* Info when disabled */}
            {!allowBrowse && (
                <Box sx={{ mb: 2, p: 1.25, borderRadius: 1.5, border: `1px solid ${COL.goldDim}`, background: 'rgba(212,175,55,0.07)' }}>
                    <Typography sx={{ fontFamily: fonts.title, color: COL.gold }}>
                        Trading is currently disabled.
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Turn it on in your <b>Profile</b> to browse tradables and send/receive trade offers.
                    </Typography>
                </Box>
            )}

            {/* Card inspect dialog (ONLY CTA: Add to Trade) */}
            <CardInfoDialog
                open={inspectOpen}
                onClose={closeInspect}
                card={inspectCard}
                readOnly
                hideCloseButton
                hideDefaultActions
                primaryActionLabel={addCtaLabel}
                onPrimaryAction={handleInspectAdd}
                primaryActionDisabled={!allowBrowse || !inspectInstance}
            />

            {/* ⭐ NEW: Active-trade viewer (0 buttons) */}
            <CardInfoDialog
                open={tradeViewOpen}
                onClose={closeTradeView}
                card={tradeViewCard}
                readOnly
                hideDefaultActions
                hideCloseButton
            />

            {/* Create Trade */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3 }}>
                {/* Recipient picker */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <GoldTextField
                        label="Find player by username"
                        value={userQuery}
                        onChange={(e) => setUserQuery(e.target.value)}
                        size="small"
                        disabled={!allowBrowse}
                    />
                    <Select
                        size="small"
                        displayEmpty
                        value={recipientId || ''}
                        onChange={(e) => {
                            const selectedRecipientId = String(e.target.value);
                            setRecipientId(selectedRecipientId);
                            const selectedUser = searchResults.find(x => x.id === selectedRecipientId);
                            setRecipientName(selectedUser?.username || '');
                        }}
                        sx={{ minWidth: 240, color: COL.text, borderColor: COL.goldDim }}
                        disabled={!allowBrowse}
                        renderValue={(value) =>
                            value
                                ? (searchResults.find(x => x.id === value)?.username || 'Select recipient')
                                : 'Select recipient'
                        }
                    >
                        {searchResults.map(u => (
                            <MenuItem key={u.id} value={u.id}>{u.username || u.id}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Offer builder */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Section>
                        <Typography sx={{ fontFamily: fonts.title, mb: 1, color: COL.gold }}>Your Offer</Typography>
                        <GoldTextField
                            label="Soul Dust"
                            type="number"
                            value={offerCreatorDust}
                            onChange={(e) => setOfferCreatorDust(Math.max(0, Number(e.target.value || 0)))}
                            size="small"
                            sx={{ mb: 2 }}
                            disabled={!allowBrowse}
                        />
                        <Grid container spacing={1}>
                            {hydrateList(offerCreatorCards).map(c => (
                                <Grid item key={c.instanceId}>
                                    <Box sx={{ position: 'relative' }}>
                                        <PlayingCard size="xs" card={c} />
                                        <GhostButton
                                            onClick={() => removeOfferCard(c.instanceId, 'creator')}
                                            size="small"
                                            sx={{ position: 'absolute', top: 4, right: 4 }}
                                        >
                                            ✕
                                        </GhostButton>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography sx={{ mt: 2, opacity: .8 }}>
                            Tap a card from <b>Your Cards</b> (below) to inspect it, then add it to your offer.
                        </Typography>
                    </Section>

                    <Section>
                        <Typography sx={{ fontFamily: fonts.title, mb: 1, color: COL.gold }}>Their Offer (requested)</Typography>
                        <GoldTextField
                            label="Soul Dust (they pay)"
                            type="number"
                            value={offerRecipientDust}
                            onChange={(e) => setOfferRecipientDust(Math.max(0, Number(e.target.value || 0)))}
                            size="small"
                            sx={{ mb: 2 }}
                            disabled={!allowBrowse}
                        />
                        <Grid container spacing={1}>
                            {hydrateList(offerRecipientCards).map(c => (
                                <Grid item key={c.instanceId}>
                                    <Box sx={{ position: 'relative' }}>
                                        <PlayingCard size="xs" card={c} />
                                        <GhostButton
                                            onClick={() => removeOfferCard(c.instanceId, 'recipient')}
                                            size="small"
                                            sx={{ position: 'absolute', top: 4, right: 4 }}
                                        >
                                            ✕
                                        </GhostButton>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography sx={{ mt: 2, opacity: .8 }}>
                            Tip: click from <b>Recipient Inventory</b> (below) to inspect and request specific cards.
                        </Typography>
                    </Section>
                </Box>

                <GoldTextField
                    label="Message (optional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={2}
                    disabled={!allowBrowse}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <GoldButton onClick={submitTrade} disabled={!allowBrowse}>Send Trade Offer</GoldButton>
                </Box>
            </Box>

            {/* Recipient Inventory (click to inspect -> add to Their Offer) */}
            <Section sx={{ mb: 3 }}>
                <SectionTitle variant="h6" gutterBottom>Recipient Inventory</SectionTitle>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.85 }}>
                    Soul Dust: <b>{recipientDust}</b>
                </Typography>
                <GoldDivider sx={{ mb: 2 }} />
                {recipientId ? (
                    <Grid container spacing={2}>
                        {recipientHydrated.map(card => (
                            <Grid item key={card.instanceId || `${card.cardId}-${card.level}-${card.evolutionStage}`}>
                                <Box
                                    sx={{
                                        cursor: allowBrowse ? 'pointer' : 'default',
                                        '&:hover': allowBrowse ? {
                                            transform: 'translateY(-2px)',
                                            transition: '150ms',
                                            filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))'
                                        } : undefined
                                    }}
                                    onClick={() => {
                                        if (!allowBrowse) return;
                                        const instance = recipientTradables.find(i => i.instanceId === card.instanceId);
                                        if (!instance) return;
                                        openInspect(card, instance, 'recipient');
                                    }}
                                >
                                    <PlayingCard size="sm" card={card} />
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontFamily: fonts.title }}>
                                        Lv {card.level} • Evo {card.evolutionStage}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                        {recipientHydrated.length === 0 && (
                            <Typography sx={{ opacity: 0.75, fontStyle: 'italic', px: 1 }}>
                                No tradable cards visible (they may have everything active, locked, or browsing disabled).
                            </Typography>
                        )}
                    </Grid>
                ) : (
                    <Typography sx={{ opacity: 0.75, fontStyle: 'italic' }}>
                        Choose a recipient to preview their tradable inventory.
                    </Typography>
                )}
            </Section>

            {/* Your Cards (click to inspect -> add to Your Offer) */}
            <Section sx={{ mb: 3 }}>
                <SectionTitle variant="h6" gutterBottom>Your Cards</SectionTitle>
                <GoldDivider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {ownedHydrated.map(card => (
                        <Grid item key={card.instanceId || `${card.cardId}-${card.level}-${card.evolutionStage}`}>
                            <Box
                                sx={{
                                    cursor: allowBrowse ? 'pointer' : 'default',
                                    '&:hover': allowBrowse ? {
                                        transform: 'translateY(-2px)',
                                        transition: '150ms',
                                        filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))'
                                    } : undefined
                                }}
                                onClick={() => {
                                    if (!allowBrowse) return;
                                    const instance = ownedInstances.find(i => i.instanceId === card.instanceId);
                                    if (!instance) return;
                                    openInspect(card, instance, 'creator');
                                }}
                            >
                                <PlayingCard size="sm" card={card} />
                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontFamily: fonts.title }}>
                                    Lv {card.level} • Evo {card.evolutionStage}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Open Trades */}
            <Section>
                <SectionTitle variant="h6" gutterBottom>Open Trades (Yours & Theirs)</SectionTitle>
                <GoldDivider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                    {trades.map(t => {
                        const youAreCreator = t.creatorId === uid;

                        const left = youAreCreator ? t.offerFromCreator : t.offerFromRecipient;
                        const right = youAreCreator ? t.offerFromRecipient : t.offerFromCreator;

                        const leftOwnerId = youAreCreator ? t.creatorId : t.recipientId;
                        const rightOwnerId = youAreCreator ? t.recipientId : t.creatorId;

                        const creatorName = t.creatorUsername || t.creatorId;
                        const recipientName2 = t.recipientUsername || t.recipientId;
                        const leftOwnerName = (leftOwnerId === t.creatorId) ? creatorName : recipientName2;
                        const rightOwnerName = (rightOwnerId === t.creatorId) ? creatorName : recipientName2;

                        const leftHeading = leftOwnerId === uid ? 'Your Offer' : `${leftOwnerName}'s Offer`;
                        const rightHeading = rightOwnerId === uid ? 'Your Offer' : `${rightOwnerName}'s Offer`;

                        const leftCards = hydrateList(left?.cards || []);
                        const rightCards = hydrateList(right?.cards || []);

                        return (
                            <Grid item key={t.id} xs={12} width='100%'>
                                <Box sx={{ borderRadius: 5, padding: 2 }}>
                                    <Typography sx={{ fontFamily: fonts.title, mb: 1 }}>
                                        {youAreCreator ? `To: ${t.recipientUsername || t.recipientId}` : `From: ${t.creatorUsername || t.creatorId}`}
                                    </Typography>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 40px 1fr' }, gap: 2, alignItems: 'center' }}>
                                        <Box>
                                            <Typography sx={{ color: COL.gold, fontFamily: fonts.title, mb: 1 }}>{leftHeading}</Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>Soul Dust: {left?.soulDust || 0}</Typography>
                                            <Grid container spacing={1}>
                                                {leftCards.map(c => (
                                                    <Grid item key={c.instanceId || `${c.cardId}-${c.level}-${c.evolutionStage}`}>
                                                        <Box
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    transition: '150ms',
                                                                    filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))'
                                                                }
                                                            }}
                                                            onClick={() => openTradeView(c)}
                                                        >
                                                            <PlayingCard size="xs" card={c} />
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        <Typography sx={{ textAlign: 'center', fontFamily: fonts.title }}>⇄</Typography>

                                        <Box>
                                            <Typography sx={{ color: COL.gold, fontFamily: fonts.title, mb: 1 }}>{rightHeading}</Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>Soul Dust: {right?.soulDust || 0}</Typography>
                                            <Grid container spacing={1}>
                                                {rightCards.map(c => (
                                                    <Grid item key={c.instanceId || `${c.cardId}-${c.level}-${c.evolutionStage}`}>
                                                        <Box
                                                            sx={{
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    transition: '150ms',
                                                                    filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.25))'
                                                                }
                                                            }}
                                                            onClick={() => openTradeView(c)}
                                                        >
                                                            <PlayingCard size="xs" card={c} />
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                                        {youAreCreator ? (
                                            <GhostButton onClick={async () => {
                                                try { await api.cancel(t.id); } catch (e) { showSnack(e?.message || 'Cancel failed'); }
                                            }}>
                                                Cancel
                                            </GhostButton>
                                        ) : (
                                            <>
                                                <GhostButton onClick={async () => {
                                                    try { await api.decline(t.id); } catch (e) { showSnack(e?.message || 'Decline failed'); }
                                                }}>
                                                    Decline
                                                </GhostButton>
                                                <GoldButton onClick={async () => {
                                                    try {
                                                        await api.accept(t.id);
                                                        showSnack('Trade accepted');
                                                        setTimeout(() => { window.location.reload(); }, 400);
                                                    } catch (e) {
                                                        showSnack(e?.message || 'Accept failed');
                                                    }
                                                }}>
                                                    Accept
                                                </GoldButton>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <GoldDivider sx={{ mb: 2, width: '100%' }} />
                            </Grid>
                        );
                    })}
                </Grid>
            </Section>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ open: false, msg: '' })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                ContentProps={{
                    sx: {
                        fontSize: '1.05rem',
                        textAlign: 'center',
                        px: 4,
                        py: 1.25,
                        backgroundColor: COL.panel,
                        color: COL.text,
                        borderRadius: 2,
                        border: `1px solid ${COL.goldDim}`,
                        boxShadow: glow(0.25),
                        fontFamily: fonts.title,
                    }
                }}
                message={snack.msg}
            />
        </Section>
    );
}
